import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Check, Clock, MapPin, Phone } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Landscape } from '@/components/generals/landscape'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'
import { useAuth } from '@/components/generals/auth-provider'
import { CONTACT_TOPICS, OFFICES } from '@/constants/contact'
import { LANDSCAPES } from '@/lib/visual'

import {
    Field,
    FormMessage,
    Honeypot,
    SelectInput,
    TextArea,
    TextInput
} from '@/components/generals/form-fields'

const ContactPage: FC = (): ReactNode => {
    const router = useRouter()
    const { member } = useAuth()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [topic, setTopic] = useState(CONTACT_TOPICS[0])
    const [message, setMessage] = useState('')
    const [website, setWebsite] = useState('')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sent, setSent] = useState(false)

    useEffect(() => {
        if (!router.isReady) return

        const requested = router.query.topic

        if (typeof requested === 'string' && CONTACT_TOPICS.includes(requested)) setTopic(requested)
    }, [router.isReady, router.query.topic])

    useEffect(() => {
        if (!member) return

        setName(current => current || member.name)
        setEmail(current => current || member.email)
    }, [member])

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError(null)

        if (!name.trim()) return setError('Please add your name so we know who to answer.')
        if (!/^\S+@\S+\.\S{2,}$/.test(email.trim())) return setError('That email address does not look quite right.')
        if (message.trim().length < 10) return setError('A few more words will help us answer well.')

        setSending(true)

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, email, topic, message, website })
            })
            const data = await response.json().catch(() => ({}))

            if (!response.ok) {
                setError(data?.message ?? 'Your message did not go through. Please try once more.')
                return
            }

            setSent(true)
        }
        catch {
            setError('We could not connect just now. Check your internet and try again.')
        }
        finally {
            setSending(false)
        }
    }

    return (
        <Layout>
            <Seo
                title='Contact'
                description='A question about your plan, your membership or an advisor call? Write to Verdane and a real person will answer within one business day.'
            />

            <section aria-labelledby='contact-title' className='relative isolate overflow-hidden pb-28 pt-36 sm:pt-44'>
                <div aria-hidden='true' className='absolute inset-0 -z-10'>
                    <Landscape src={LANDSCAPES.loneTree} fade='none' position='center 80%' />
                    <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.75)_0%,rgba(0,0,0,0.45)_35%,rgba(0,0,0,0.6)_70%,#000_100%)]' />
                </div>

                <div className='shell grid gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-16'>
                    <div>
                        <Eyebrow align='left'>
                            Get in touch
                        </Eyebrow>

                        <SplitHeading
                            as='h1'
                            id='contact-title'
                            trigger='mount'
                            delay={150}
                            text='Talk to a person, *not a queue*'
                            className='mt-6 text-[2.6rem] font-normal leading-[1] tracking-[-0.045em] text-cream sm:text-6xl'
                        />

                        <Reveal trigger='mount' delay={600} className='mt-10'>
                            {sent
                                ? (
                                    <div className='glass-dark grain relative overflow-hidden rounded-[30px] p-8 sm:p-10' role='status'>
                                        <span className='grid size-12 place-items-center rounded-full bg-citron text-black'>
                                            <Check aria-hidden='true' className='size-5' />
                                        </span>

                                        <h2 className='mt-6 font-serif text-4xl italic text-cream'>
                                            Thanks, {name.split(' ')[0]}
                                        </h2>

                                        <p className='mt-3 max-w-md text-[15px] leading-relaxed text-cream/70'>
                                            Someone on our team has your note now. Expect a reply at {email} within one business day, often sooner.
                                        </p>

                                        <div className='mt-8 flex flex-wrap items-center gap-5'>
                                            <PillButton href='/ai-intelligence#demo'>
                                                Ask the AI while you wait
                                            </PillButton>

                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setSent(false)
                                                    setMessage('')
                                                }}
                                                className='text-sm text-cream/65 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'
                                            >
                                                Write another message
                                            </button>
                                        </div>
                                    </div>
                                )
                                : (
                                    <form onSubmit={submit} noValidate className='glass-dark grain relative overflow-hidden rounded-[30px] p-6 sm:p-8'>
                                        <div className='grid gap-4 sm:grid-cols-2'>
                                            <Field id='contact-name' label='Full name'>
                                                <TextInput id='contact-name' name='name' autoComplete='name' required maxLength={80} value={name} onChange={event => setName(event.target.value)} />
                                            </Field>

                                            <Field id='contact-email' label='Email address'>
                                                <TextInput id='contact-email' name='email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />
                                            </Field>
                                        </div>

                                        <Field id='contact-topic' label='What is it about?' className='mt-4'>
                                            <SelectInput id='contact-topic' name='topic' value={topic} onChange={event => setTopic(event.target.value)}>
                                                {CONTACT_TOPICS.map(option => <option key={option} value={option}>{option}</option>)}
                                            </SelectInput>
                                        </Field>

                                        <Field id='contact-message' label='Your message' className='mt-4'>
                                            <TextArea id='contact-message' name='message' rows={6} required maxLength={4000} value={message} onChange={event => setMessage(event.target.value)} />
                                        </Field>

                                        <Honeypot value={website} onChange={setWebsite} />

                                        {error && <FormMessage className='mt-5'>{error}</FormMessage>}

                                        <div className='mt-6 flex flex-wrap items-center justify-between gap-4'>
                                            <p className='max-w-xs text-xs leading-relaxed text-cream/45'>
                                                Your details are used for this reply and nothing else. Read our{' '}
                                                <Link href='/privacy' className='underline decoration-cream/30 underline-offset-2 hover:text-cream'>
                                                    privacy policy
                                                </Link>
                                                .
                                            </p>

                                            <PillButton type='submit' loading={sending}>
                                                Send to our team
                                            </PillButton>
                                        </div>
                                    </form>
                                )}
                        </Reveal>
                    </div>

                    <div className='lg:pt-28'>
                        <Reveal trigger='mount' delay={800} className='glass grain relative overflow-hidden rounded-[30px] p-6 sm:p-8'>
                            <p className='text-[11px] font-medium uppercase tracking-[0.3em] text-cream/50'>
                                Where to find us
                            </p>

                            <ul className='mt-6 divide-y divide-white/10'>
                                {OFFICES.map(office => (
                                    <li key={office.city} className='py-5 first:pt-0 last:pb-0'>
                                        <h2 className='font-serif text-2xl italic text-cream'>
                                            {office.city}
                                        </h2>

                                        <div className='mt-3 space-y-2 text-sm text-cream/70'>
                                            <p className='flex items-start gap-2.5'>
                                                <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-citron' />
                                                <span>
                                                    {office.address[0]}
                                                    <br />
                                                    {office.address[1]}
                                                </span>
                                            </p>

                                            <p className='flex items-center gap-2.5'>
                                                <Clock aria-hidden='true' className='size-4 shrink-0 text-citron' />
                                                {office.hours}
                                            </p>

                                            <p className='flex items-center gap-2.5'>
                                                <Phone aria-hidden='true' className='size-4 shrink-0 text-citron' />
                                                <a href={`tel:${office.phone.replace(/[^\d+]/g, '')}`} className='hover:text-cream'>
                                                    {office.phone}
                                                </a>
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>

                        <Reveal trigger='mount' delay={950} className='mt-5 rounded-[26px] bg-white/[0.04] p-6 ring-1 ring-white/10'>
                            <p className='text-[15px] text-cream'>
                                Would a conversation help more?
                            </p>

                            <p className='mt-1 text-sm text-cream/55'>
                                The first 30 minute advisor call is on us.
                            </p>

                            <Link href='/find-an-advisor' className='mt-4 inline-block text-sm text-cream/80 underline decoration-cream/30 underline-offset-[6px] hover:text-cream hover:decoration-citron'>
                                Find your advisor
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export default ContactPage
